import { match } from 'ts-pattern';
import { cn } from '@/lib/utils';
import { CMSText } from '../cms-text';
import { getCardThemeStyles } from './transforms.ts';
import type { CMSCardProps } from './index.tsx';
import { CMSCardImagePosition } from '../schemas.ts';
import { AverageActivation } from '@/components/average-activation/index.tsx';
import { HTMLContent } from '@/components/ui/html-content/index.tsx';
import { CMSCTAButton } from '../cms-cta-button/index.tsx';

const horizontalViewSize = 'max-w-[548px]';

const CMSHorizontalCard = ({ cardData, className, style }: CMSCardProps) => {
  const showCtaOptions = cardData.show_cta_options;
  const imagePosition = cardData.feature_image.image_position.key;
  const showImageOptions = cardData.show_image_options;
  const theme = cardData.theme.key;
  const themeColour = getCardThemeStyles(theme);

  return (
    <div
      data-component="CMSHorizontalCard"
      style={style}
      className={cn(
        className,
        themeColour,
        horizontalViewSize,
        'grid grid-rows-[48px,1fr] p-4 rounded-[16px] lg:rounded-[24px] lg:p-8 min-w-full',
        'md:grid-rows-[28px,1fr] md:grid-cols-[71px_minmax(179px,_1fr)_0.5fr] md:p-[24px]',
        'lg:grid-cols-[0.82fr_2fr_0.5fr]',
        {
          'grid-cols-[56px_1fr_20px]':
            showImageOptions &&
            imagePosition === CMSCardImagePosition.Enum.start,
        },
        {
          'grid-cols-[20px_1fr_56px] md:grid-cols-[minmax(179px,_1fr)_0.5fr_71px] lg:grid-cols-[2fr_0.82fr_0.5fr]':
            showImageOptions && imagePosition === CMSCardImagePosition.Enum.end,
        }
      )}
    >
      {/* FIXME: should we add max-w here for the images? */}
      {showImageOptions && cardData.feature_image.feature_image?.url && (
        <div
          className={cn(
            'self-center place-items-center pr-2 lg:pr-4 shrink-0',
            {
              'col-start-3 col-end-4':
                imagePosition === CMSCardImagePosition.Enum.end,
            },
            'md:row-start-1 md:row-end-4'
          )}
        >
          <img
            className="w-full h-full"
            src={cardData.feature_image.feature_image.url}
            alt={cardData.feature_image.feature_image.alt ?? 'card image'}
            loading="lazy"
          />
        </div>
      )}
      <div
        className={cn(
          'text-balance text-white self-center row-start-1 row-end-2 text-base  font-nunito700 lg:text-xl heading',
          { 'col-start-1 col-end-4': !showCtaOptions && !showImageOptions },
          {
            'col-start-2 col-end-3':
              imagePosition === CMSCardImagePosition.Enum.start &&
              showCtaOptions &&
              showImageOptions,
          },
          {
            'col-start-1 col-end-2':
              showImageOptions &&
              imagePosition === CMSCardImagePosition.Enum.end &&
              showCtaOptions,
          },
          {
            'col-start-2 col-end-4 md:col-start-2 md:col-end-4':
              showImageOptions &&
              imagePosition === CMSCardImagePosition.Enum.start &&
              !showCtaOptions,
          },
          {
            'col-start-1 col-end-3':
              (showImageOptions &&
                imagePosition === CMSCardImagePosition.Enum.end &&
                !showCtaOptions) ||
              (!showImageOptions && showCtaOptions),
          }
        )}
      >
        <HTMLContent content={cardData?.heading ?? ''} />
      </div>
      {showCtaOptions && cardData.cta.icon_text && (
        <div
          className={cn(
            'col-start-3 place-self-end self-center row-start-1 row-end-2',
            'md:row-start-1 md:row-end-4 md:pr-[4.11px]',
            'lg:pr-0',
            {
              'col-start-2 col-end-3':
                showImageOptions &&
                imagePosition === CMSCardImagePosition.Enum.end,
            }
          )}
        >
          <CMSCTAButton
            className="w-full iconButton md:[&_.iconButton]:px-0"
            ctaData={cardData.cta}
          />
        </div>
      )}
      {cardData.content.map((item, index) =>
        match(item)
          .with({ type: 'text' }, (item) => (
            <CMSText
              key={index}
              textData={item}
              className={cn(
                'col-span-3 row-start-2 row-end-2 mt-[8px]',

                // Negate margin bottom for p tag coming from CMSColumn
                '[&_p]:!mb-0',

                // Negate text size for p tag coming from CMSColumn
                '[&_p]:!text-sm md:[&_p]:!text-sm lg:[&_p]:!text-base',
                {
                  'md:col-start-1 md:col-end-4':
                    !showCtaOptions && !showImageOptions,
                },
                {
                  'md:col-start-2 md:col-end-3':
                    showImageOptions &&
                    imagePosition === CMSCardImagePosition.Enum.start &&
                    showCtaOptions,
                },

                {
                  'md:col-start-1 md:col-end-3':
                    showCtaOptions && !showImageOptions,
                },
                {
                  'md:col-start-2 md:col-end-4':
                    showImageOptions &&
                    imagePosition === CMSCardImagePosition.Enum.start &&
                    !showCtaOptions,
                },

                {
                  'md:col-start-1 md:col-end-3':
                    showImageOptions &&
                    imagePosition === CMSCardImagePosition.Enum.end &&
                    !showCtaOptions,
                },

                {
                  'md:col-start-1 md:col-end-2':
                    showImageOptions &&
                    imagePosition === CMSCardImagePosition.Enum.end &&
                    showCtaOptions,
                }
              )}
            />
          ))
          .with({ type: 'average_activation' }, () => (
            <div
              key={index}
              className={cn(
                ' font-nunito700 row-start-3 mt-[20px] col-start-1 col-end-4 md:col-start-2 md:col-end-4',
                'md:mt-[8px]',
                'lg:mt-[16px]',
                {
                  'col-start-1 col-end-4 md:col-start-1 md:col-end-4':
                    !showCtaOptions && !showImageOptions,
                },
                {
                  'md:col-start-1 md:col-end-2':
                    showImageOptions &&
                    imagePosition === CMSCardImagePosition.Enum.end &&
                    showCtaOptions,
                },
                {
                  'md:col-start-1 md:col-end-3':
                    (showImageOptions &&
                      imagePosition === CMSCardImagePosition.Enum.end &&
                      !showCtaOptions) ||
                    (!showImageOptions && showCtaOptions),
                }
              )}
            >
              <AverageActivation />
            </div>
          ))
          .otherwise(() => null)
      )}
    </div>
  );
};

export { CMSHorizontalCard };
